import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Palette, Loader2, Info, ChevronDown, ChevronRight, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API, authFetch } from "@/App";
import { FabricReviewRow } from "./FabricReviewRow";
import { useFabricLibrary, refreshFabricLibrary } from "./useFabricLibrary";

const postJson = (url, body) => authFetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });

// Verification screen: email image vs library image for each fabric, before anything is saved.
export function FabricReviewModal({ open, orderId, orderName, candidates, onClose, onSaved }) {
  const library = useFabricLibrary();
  const [rows, setRows] = useState([]);
  const [showOthers, setShowOthers] = useState(false);
  const [saving, setSaving] = useState(false);
  const refByKey = useMemo(() => new Map((candidates?.refs || []).map((r) => [r.key, r])), [candidates]);

  useEffect(() => {
    if (open && candidates) setRows(candidates.items.map((it) => ({ ...it, attach: !!it.key, addToLibrary: !!it.key, replace: false })));
  }, [open, candidates]);

  if (!open || !candidates) return null;

  const update = (id, patch) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const assigned = rows.filter((r) => r.key);
  const others = rows.filter((r) => !r.key);

  const save = async () => {
    setSaving(true);
    let attached = 0; let added = 0; let failed = 0;
    for (const row of rows) {
      const ref = row.key ? refByKey.get(row.key) : null;
      if (row.attach) {
        const res = await postJson(`${API}/orders/${orderId}/attachments/from-source`, {
          source: row.src, filename: ref ? `toile_${ref.code}` : row.filename, print_with_sheet: true,
          fabric_key: row.key, fabric_label: ref?.label || null,
        });
        if (res.ok) attached += 1; else failed += 1;
      }
      const hasSwatch = !!(row.key && library.byKey.get(row.key));
      if (ref && (hasSwatch ? row.replace : row.addToLibrary)) {
        const res = await postJson(`${API}/fabric-swatches/from-source`, { collection: ref.collection, code: ref.code, source: row.src, replace: hasSwatch });
        if (res.ok) { const d = await res.json(); if (!d.skipped) added += 1; } else failed += 1;
      }
    }
    await refreshFabricLibrary();
    setSaving(false);
    if (failed) toast.error(`${failed} image(s) n'ont pas pu être récupérées. Essayez avec le fichier .eml / .msg.`);
    if (attached || added) toast.success(`${attached} photo(s) jointe(s) à la commande, ${added} ajoutée(s) à la bibliothèque`);
    await onSaved?.();
    onClose();
  };

  const renderRow = (row) => (
    <FabricReviewRow key={row.id} idx={rows.indexOf(row)} row={row} refs={candidates.refs}
      swatch={row.key ? library.byKey.get(row.key) || null : null} onChange={(patch) => update(row.id, patch)} />
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4" data-testid="fabric-review-modal">
      <div className="bg-zinc-50 rounded-xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-5 py-4 border-b border-zinc-200 bg-white rounded-t-xl">
          <div>
            <h3 className="font-semibold text-zinc-900 font-['Manrope'] flex items-center gap-2"><Palette className="w-5 h-5 text-orange-500" />Vérification des toiles{orderName ? ` — ${orderName}` : ""}</h3>
            <p className="text-xs text-zinc-500 mt-0.5">Comparez la photo de l'email avec celle de la bibliothèque avant d'enregistrer.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-lg transition-colors" data-testid="fabric-review-close"><X className="w-5 h-5 text-zinc-500" /></button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-3">
          {candidates.unavailable > 0 && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-800 text-xs" data-testid="fabric-review-unavailable">
              <Info className="w-4 h-4 flex-shrink-0" />
              <span>{candidates.unavailable} image(s) n'ont pas pu être copiées depuis la messagerie. Pour les récupérer, glissez le fichier de l'email (.msg / .eml).</span>
            </div>
          )}
          {assigned.length === 0 && <p className="text-sm text-zinc-500" data-testid="fabric-review-no-match">Aucune image n'a pu être associée automatiquement à une toile. Choisissez la toile dans les images ci-dessous.</p>}
          {assigned.map(renderRow)}
          {others.length > 0 && (
            <div>
              <button type="button" onClick={() => setShowOthers((v) => !v)} className="flex items-center gap-1 text-sm font-medium text-zinc-600 hover:text-zinc-900 py-1" data-testid="fabric-review-toggle-others">
                {showOthers || assigned.length === 0 ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                Autres images de l'email ({others.length})
              </button>
              {(showOthers || assigned.length === 0) && <div className="space-y-3 mt-2">{others.map(renderRow)}</div>}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-zinc-200 bg-white rounded-b-xl">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving} data-testid="fabric-review-skip">Ignorer</Button>
          <Button size="sm" onClick={save} disabled={saving} className="gap-2 bg-orange-500 hover:bg-orange-600" data-testid="fabric-review-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
}
